import React from "react";
// Chakra imports
import {
    Box,
    Flex,
    Button,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Switch,
    Text,
    FormErrorMessage,
    useToast,
    VStack,
    Icon,
    InputGroup,
    InputLeftElement,
    Image,
    useColorModeValue,
} from "@chakra-ui/react";
import { useLoading } from "Providers/LoadingProvider";
import { Link, useHistory } from "react-router-dom";
import { useAuth } from "Providers/AuthProvider";
import { Field, Form, Formik } from "formik";
import AuthApi from "Api/AuthApi";
import Storage from "Helpers/Storage";
import { TbMail, TbLock, TbLogin } from "react-icons/tb";

function SignIn() {
    const loading = useLoading();
    const history = useHistory();
    const auth = useAuth();
    const toast = useToast({
        position: "top-right",
    });

    let submitHandle = async (values) => {
        loading.show();
        let response = null;
        try {
            response = await AuthApi.signIn(values);
        } catch (err) {
            response = err.response;
        }

        if (response.status !== 200) {
            if (response.status === 401)
                toast({ status: 'error', title: 'Usuário ou senha incorretos!', duration: 3000, variant: 'solid', isClosable: true });
            else
                toast({ status: 'error', title: 'Erro ao autenticar!', duration: 3000, variant: 'solid', isClosable: true });

            loading.hide();
        } else {
            Storage.set('token', response.json.token);
            try {
                let responseMe = await AuthApi.me();
                if (responseMe.status !== 200) {
                    auth.logout('Erro ao obter informações do servidor, entre em contato com o suporte.');
                } else {
                    auth.login(responseMe.json.data);
                    window.gtag('event', 'login');
                    setTimeout( () => {
                        toast({ status: 'success', title: 'Login realizado com sucesso! Bem-vindo.', duration: 3000, variant: 'solid', isClosable: true });
                        history.push("/members/home");
                    }, 300);
                }
            } finally {
                loading.hide();
            }
        }
    }

    // Dark mode colors
    const cardBg = useColorModeValue("white", "gray.800");
    const inputBg = useColorModeValue("gray.50", "gray.700");
    const inputBorder = useColorModeValue("gray.200", "gray.600");
    const inputHoverBorder = useColorModeValue("gray.300", "gray.500");
    const inputFocusBg = useColorModeValue("white", "gray.700");
    const labelColor = useColorModeValue("gray.700", "gray.200");
    const textMuted = useColorModeValue("gray.500", "gray.400");
    const iconColor = useColorModeValue("gray.400", "gray.500");
    const linkColor = useColorModeValue("#2D6B3F", "green.300");

    return (
        <Flex w="100%" justify="center" px={4}>
            <Box
                w={{ base: "100%", md: "420px" }}
                bg={cardBg}
                borderRadius="2xl"
                boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                overflow="hidden"
            >
                {/* Header */}
                <Box
                    bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                    py={8}
                    px={6}
                    textAlign="center"
                >
                    <Link to="/">
                        <Image
                            src="/wsclima-logo.png"
                            alt="WS Clima Logo"
                            h="60px"
                            w="auto"
                            mx="auto"
                            mb={3}
                            objectFit="contain"
                        />
                    </Link>
                    <Heading color="white" fontSize="2xl" fontWeight="bold">
                        Bem-vindo de volta
                    </Heading>
                    <Text color="whiteAlpha.800" fontSize="sm" mt={2}>
                        Digite suas credenciais para acessar sua conta
                    </Text>
                </Box>

                {/* Form */}
                <Box p={8}>
                    <Formik onSubmit={submitHandle} initialValues={{ email: "", password: "", remember: false }}>
                        <Form>
                            <VStack spacing={5}>
                                <Field name="email">
                                    {({ field, form }) => (
                                        <FormControl isInvalid={form.errors.email && form.touched.email}>
                                            <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                                                E-mail
                                            </FormLabel>
                                            <InputGroup size="lg">
                                                <InputLeftElement pointerEvents="none" h="100%">
                                                    <Icon as={TbMail} color={iconColor} />
                                                </InputLeftElement>
                                                <Input
                                                    {...field}
                                                    borderRadius="xl"
                                                    fontSize="sm"
                                                    type="email"
                                                    placeholder="seu@email.com"
                                                    bg={inputBg}
                                                    border="2px solid"
                                                    borderColor={inputBorder}
                                                    _hover={{ borderColor: inputHoverBorder }}
                                                    _focus={{ borderColor: "#2D6B3F", bg: inputFocusBg, boxShadow: "0 0 0 1px #2D6B3F" }}
                                                />
                                            </InputGroup>
                                            <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>

                                <Field name="password">
                                    {({ field, form }) => (
                                        <FormControl isInvalid={form.errors.password && form.touched.password}>
                                            <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                                                Senha
                                            </FormLabel>
                                            <InputGroup size="lg">
                                                <InputLeftElement pointerEvents="none" h="100%">
                                                    <Icon as={TbLock} color={iconColor} />
                                                </InputLeftElement>
                                                <Input
                                                    {...field}
                                                    borderRadius="xl"
                                                    fontSize="sm"
                                                    type="password"
                                                    placeholder="••••••••"
                                                    bg={inputBg}
                                                    border="2px solid"
                                                    borderColor={inputBorder}
                                                    _hover={{ borderColor: inputHoverBorder }}
                                                    _focus={{ borderColor: "#2D6B3F", bg: inputFocusBg, boxShadow: "0 0 0 1px #2D6B3F" }}
                                                />
                                            </InputGroup>
                                            <FormErrorMessage>{form.errors.password}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>

                                <Field name="remember">
                                    {({ field, form }) => (
                                        <FormControl display="flex" alignItems="center" isInvalid={form.errors.remember && form.touched.remember}>
                                            <Switch
                                                id="remember"
                                                colorScheme="green"
                                                me="10px"
                                                {...field}
                                            />
                                            <FormLabel htmlFor="remember" mb="0" fontSize="sm" fontWeight="normal" color={textMuted}>
                                                Manter logado
                                            </FormLabel>
                                            <FormErrorMessage>{form.errors.remember}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>

                                <Button
                                    type="submit"
                                    bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                                    color="white"
                                    w="100%"
                                    h="50px"
                                    borderRadius="xl"
                                    fontSize="md"
                                    fontWeight="semibold"
                                    _hover={{
                                        bg: "linear-gradient(135deg, #1F5A2F 0%, #164A24 100%)",
                                        transform: "translateY(-2px)",
                                        boxShadow: "lg",
                                    }}
                                    _active={{
                                        transform: "translateY(0)",
                                    }}
                                    transition="all 0.2s"
                                    leftIcon={<TbLogin />}
                                >
                                    Entrar
                                </Button>
                            </VStack>
                        </Form>
                    </Formik>

                    {/* Links */}
                    <VStack spacing={3} mt={6}>
                        <Link to="/auth/recover">
                            <Text color={linkColor} fontSize="sm" fontWeight="medium" _hover={{ textDecoration: "underline" }}>
                                Esqueci minha senha
                            </Text>
                        </Link>
                        <Text color={textMuted} fontSize="sm" textAlign="center">
                            Ainda não tem uma conta?{" "}
                            <Link to="/auth/signup">
                                <Text as="span" color={linkColor} fontWeight="bold" _hover={{ textDecoration: "underline" }}>
                                    Teste grátis por 30 dias
                                </Text>
                            </Link>
                        </Text>
                    </VStack>
                </Box>
            </Box>
        </Flex>
    );
}

export default SignIn;
